import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, FileDown } from "lucide-react";
import { fileUrlWithAuth } from "@/components/PhotoUploader";
import { useAuth } from "@/contexts/AuthContext";
import { ViewerUnlockCard } from "@/components/ViewerUnlock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDateIT = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export default function RapportinoPrintPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/rapportini/${id}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    document.title = data
      ? `Rapp. N° ${data.numero} - ${data.data_inizio || data.data}`
      : "Rapportino";
    return () => {
      document.title = "IL-Report";
    };
  }, [data]);

  if (loading) {
    return <div className="p-10 text-center text-slate-500">Caricamento…</div>;
  }
  // Visualizzatore: la stampa (contiene firme/foto) richiede la password
  if (user?.role === "viewer" && !user?.view_unlocked) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center p-6">
        <ViewerUnlockCard
          title="Stampa protetta"
          description="Inserisci di nuovo la tua password per vedere e stampare il rapportino."
          onUnlocked={() => window.location.reload()}
        />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-10 text-center text-slate-500">
        Rapportino non trovato.
      </div>
    );
  }

  const giorni =
    data.giorni && data.giorni.length
      ? data.giorni
      : [
          {
            data: data.data_inizio || data.data,
            righe: data.righe || [],
            annotazioni: data.annotazioni,
            tot_pasti: data.tot_pasti,
            mezzi_guidatori: data.mezzi_guidatori,
            pranzo: data.pranzo,
            pernotto: data.pernotto,
          },
        ];

  return (
    <div className="bg-white">
      {/* Toolbar (no print) */}
      <div className="no-print sticky top-0 bg-white border-b border-slate-200 z-30">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/">
              <Button variant="outline" size="icon" className="h-11 w-11">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                Anteprima stampa
              </div>
              <div className="font-heading font-bold tracking-tight">
                Rapp. N° {data.numero}
                {data.data_inizio === data.data_fine
                  ? ` · ${data.data_inizio || data.data}`
                  : ` · ${data.data_inizio} → ${data.data_fine}`}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() =>
                window.open(
                  `${BACKEND_URL}/api/rapportini/${data.id}/pdf?auth=${encodeURIComponent(localStorage.getItem("rapportini_token") || "")}`,
                  "_blank"
                )
              }
              data-testid="download-pdf-button"
              className="h-12"
            >
              <FileDown className="h-5 w-5 mr-2" /> PDF server
            </Button>
            <Button
              onClick={() => window.print()}
              data-testid="print-now-button"
              className="h-12 bg-[#0044FF] hover:bg-[#0033CC]"
            >
              <Printer className="h-5 w-5 mr-2" /> Stampa / PDF
            </Button>
          </div>
        </div>
      </div>

      {giorni.map((day, idx) => (
        <div
          key={`day-${day?.data || idx}-${idx}`}
          className="max-w-[1100px] mx-auto p-6 md:p-10 print-page"
        >
          <PrintSheet
            data={data}
            day={day}
            isLast={idx === giorni.length - 1}
            formatDateIT={formatDateIT}
          />
        </div>
      ))}
    </div>
  );
}

function PrintSheet({ data, day, isLast, formatDateIT }) {
  const totali = (day.righe || []).reduce(
    (acc, r) => {
      acc.guida += Number(r.ore_guida || 0);
      acc.passegg += Number(r.ore_passegg || 0);
      acc.lavoro += Number(r.ore_lavoro || 0);
      acc.caldo += Number(r.ore_caldo || 0);
      acc.notturne += Number(r.ore_notturne || 0);
      acc.tot += Number(r.ore_tot || 0);
      return acc;
    },
    { guida: 0, passegg: 0, lavoro: 0, caldo: 0, notturne: 0, tot: 0 }
  );
  const minRows = 6;
  const padded = [
    ...(day.righe || []),
    ...Array(Math.max(0, minRows - (day.righe || []).length)).fill(null),
  ];

  return (
    <div
      className="border-2 border-black p-3"
      style={{ fontFamily: "IBM Plex Sans, sans-serif" }}
    >
      <div className="grid grid-cols-12 border-2 border-black">
        <div className="col-span-6 p-3 border-r-2 border-black">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-700">
            IL-Report
          </div>
          <div className="font-heading font-black text-xl uppercase tracking-tight mt-1">
            Rapportino interno di lavoro
          </div>
        </div>
        <div className="col-span-3 p-3 border-r-2 border-black">
          <div className="text-[10px] uppercase font-bold text-slate-700">
            Rapp. N°
          </div>
          <div className="font-heading font-black text-3xl">{data.numero}</div>
        </div>
        <div className="col-span-3 p-3">
          <div className="text-[10px] uppercase font-bold text-slate-700">
            Data
          </div>
          <div className="font-heading font-bold text-base capitalize">
            {formatDateIT(day.data)}
          </div>
        </div>
      </div>

      <table
        className="w-full border-2 border-black border-t-0 print-table"
        style={{ borderCollapse: "collapse" }}
      >
        <thead>
          <tr className="bg-slate-100 text-[11px]">
            <th className="border border-black p-1 text-left w-[14%]">COMMESSA</th>
            <th className="border border-black p-1 text-left w-[20%]">PERSONALE</th>
            <th colSpan={6} className="border border-black p-1 text-center">
              ORE
            </th>
            <th className="border border-black p-1 text-left">NOTE</th>
          </tr>
          <tr className="bg-slate-50 text-[10px]">
            <th className="border border-black p-1"></th>
            <th className="border border-black p-1"></th>
            <th className="border border-black p-1 w-[6%]">GUIDA</th>
            <th className="border border-black p-1 w-[6%]">PASSEGG.</th>
            <th className="border border-black p-1 w-[6%]">LAVORO</th>
            <th className="border border-black p-1 w-[6%]" title="Ore al caldo">🔥</th>
            <th className="border border-black p-1 w-[6%]" title="Ore notturne">🌙</th>
            <th className="border border-black p-1 w-[6%]">TOT</th>
            <th className="border border-black p-1"></th>
          </tr>
        </thead>
        <tbody>
          {padded.map((riga, idx) => (
            <tr key={`row-${idx}`} style={{ height: "28px" }} className="text-[11px]">
              <td className="border border-black px-2 font-mono">
                {riga?.commessa_codice || ""}
              </td>
              <td className="border border-black px-2">
                {riga?.employee_nome || ""}
              </td>
              <td className="border border-black px-2 text-right font-mono">
                {riga?.ore_guida ? Number(riga.ore_guida).toFixed(2) : ""}
              </td>
              <td className="border border-black px-2 text-right font-mono">
                {riga?.ore_passegg ? Number(riga.ore_passegg).toFixed(2) : ""}
              </td>
              <td className="border border-black px-2 text-right font-mono">
                {riga?.ore_lavoro ? Number(riga.ore_lavoro).toFixed(2) : ""}
              </td>
              <td className="border border-black px-2 text-right font-mono text-orange-700">
                {riga?.ore_caldo ? Number(riga.ore_caldo).toFixed(2) : ""}
              </td>
              <td className="border border-black px-2 text-right font-mono text-indigo-700">
                {riga?.ore_notturne ? Number(riga.ore_notturne).toFixed(2) : ""}
              </td>
              <td className="border border-black px-2 text-right font-mono font-bold">
                {riga?.ore_tot ? Number(riga.ore_tot).toFixed(2) : ""}
              </td>
              <td className="border border-black px-2">{riga?.note || ""}</td>
            </tr>
          ))}
          <tr className="bg-slate-100 text-[11px]">
            <td className="border border-black px-2 font-bold" colSpan={2}>
              TOTALI GIORNATA
            </td>
            <td className="border border-black px-2 text-right font-bold font-mono">
              {totali.guida.toFixed(2)}
            </td>
            <td className="border border-black px-2 text-right font-bold font-mono">
              {totali.passegg.toFixed(2)}
            </td>
            <td className="border border-black px-2 text-right font-bold font-mono">
              {totali.lavoro.toFixed(2)}
            </td>
            <td className="border border-black px-2 text-right font-bold font-mono text-orange-700">
              {totali.caldo.toFixed(2)}
            </td>
            <td className="border border-black px-2 text-right font-bold font-mono text-indigo-700">
              {totali.notturne.toFixed(2)}
            </td>
            <td className="border border-black px-2 text-right font-bold font-mono">
              {totali.tot.toFixed(2)}
            </td>
            <td className="border border-black"></td>
          </tr>
        </tbody>
      </table>

      <div className="grid grid-cols-12 border-2 border-black border-t-0">
        <div className="col-span-8 border-r-2 border-black p-2">
          <div className="text-[10px] uppercase font-bold tracking-wide">
            Annotazioni
          </div>
          <div className="text-[11px] mt-1 min-h-[60px] whitespace-pre-wrap">
            {day.annotazioni || ""}
          </div>
        </div>
        <div className="col-span-4 grid grid-rows-2">
          <div className="border-b-2 border-black p-2">
            <div className="text-[10px] uppercase font-bold">Tot. pasti</div>
            <div className="font-heading font-bold text-lg">
              {day.tot_pasti || 0}
            </div>
          </div>
          <div className="p-2">
            <div className="text-[10px] uppercase font-bold">
              Mezzi / Guidatori
            </div>
            <div className="text-[11px] mt-1 whitespace-pre-wrap">
              {day.mezzi_guidatori || ""}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 border-2 border-black border-t-0">
        <div className="border-r-2 border-black p-2">
          <div className="text-[10px] uppercase font-bold">Pranzo</div>
          <div className="text-[11px] mt-1">{day.pranzo || ""}</div>
        </div>
        <div className="border-r-2 border-black p-2">
          <div className="text-[10px] uppercase font-bold">Pernotto</div>
          <div className="text-[11px] mt-1">{day.pernotto || ""}</div>
        </div>
        <div className="p-2">
          <div className="text-[10px] uppercase font-bold">Firma</div>
          {data.firma_immagine ? (
            <div className="mt-1">
              <img
                src={data.firma_immagine}
                alt="Firma"
                className="max-h-12 mx-auto"
              />
              <div className="text-[9px] text-slate-500 text-center mt-0.5">
                {data.firma}
                {data.firma_data
                  ? ` · ${new Date(data.firma_data).toLocaleString("it-IT")}`
                  : ""}
              </div>
            </div>
          ) : data.firma ? (
            <div className="text-[11px] mt-1">
              <div className="font-bold">{data.firma}</div>
              <div className="text-[9px] text-slate-500">
                {data.firma_data
                  ? new Date(data.firma_data).toLocaleString("it-IT")
                  : ""}
              </div>
            </div>
          ) : (
            <div className="text-[10px] text-slate-400 italic mt-1">
              (Non firmato)
            </div>
          )}
        </div>
      </div>

      {/* Foto (only on last page to avoid duplication) */}
      {isLast && data.foto && data.foto.length > 0 && (
        <div className="border-2 border-black border-t-0 p-2">
          <div className="text-[10px] uppercase font-bold mb-2">
            Foto allegate ({data.foto.length})
          </div>
          <div className="grid grid-cols-4 gap-2">
            {data.foto.map((p, idx) => (
              <div
                key={p || `foto-${idx}`}
                className="border border-slate-300 aspect-square overflow-hidden"
              >
                <img
                  src={fileUrlWithAuth(p)}
                  alt={`Foto ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-[9px] text-slate-500 mt-2 flex justify-between">
        <span>Compilato da: {data.created_by_name || "-"}</span>
        <span>Documento generato da IL-Report</span>
      </div>
    </div>
  );
}
